// src/components/WOE/WOEPayment/WOEPayment.jsx - Simplified version
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../WOE.css'; // Go up one level

// Modular hooks
import useAgreementManager from './hooks/useAgreementManager';
import usePaymentCalculations from './hooks/usePaymentCalculations';
import useOrderSubmission from './hooks/useOrderSubmission';

// import { getUserWarehouse } from '../../../constants/userLocation';

// Services
import validationService from './services/validationService';

// Utils
import { isIsraelUser, requiresTaxCalculation } from '../../../utils/locationUtils';
import { normalizeOrderType } from '../../../utils/orderTypeUtils';
import {  formatDate } from '../../../utils/dateUtils'; //(e.g., "Jan 15, 2025")
import { getCurrentUser } from '../../../utils/auth';
import { API_URL } from '../../../config/api';
import { orderExists } from '../../../utils/statePersistence';
import { CLOVER_ALLOWED_USERS } from '../../../utils/userUtils';

import CustomerAgreementModal from '../CustomerAgreementModal'; // Adjust path as needed
import SaveCardModal from '../../WorkOrderProfile/SaveCardModal';


export const requiresAgreement = (orderType) => {
    const agreementRequiredTypes = ['New Wig', 'New Sale Wig'];
    // "Aidel New Wig" follows the same agreement rules as "New Wig".
    return agreementRequiredTypes.includes(normalizeOrderType(orderType));
};

const WOEPayment = ({
    customerInfo,
    wigDetails,
    wigData,
    wigRoomsData = [],
    orderItems = [],
    accessories = [],
    barcode,
    serialCode,
    orderNo,
    savedPaymentData = null,
    onOrderNoUpdate,
    onPrevious,
    onSubmit,
    onStateChange, // 🔧 NEW: Callback for continuous state updates
    appointmentId, // 🔗 NEW: Appointment ID for linking
    currentOrderId // 🔧 NEW: Order ID for cross-tab submission prevention
}) => {
    const navigate = useNavigate();

     // State
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [showTaxSelector, setShowTaxSelector] = useState(false);
    const [isLoadingUserLocation, setIsLoadingUserLocation] = useState(false);

    // Deposit bypass state (New Wig orders only)
    const [depositBypassActive, setDepositBypassActive] = useState(false);
    const [depositBypassReason, setDepositBypassReason] = useState('');
    const [showBypassModal, setShowBypassModal] = useState(false);
    const [bypassReasonInput, setBypassReasonInput] = useState('');

    // 🆕 CLOVER: Saved cards state
    const [cloverCards, setCloverCards] = useState([]);
    const [savedCardSelections, setSavedCardSelections] = useState({}); // Track saved card per payment: { paymentId: cardId }
    const [showSaveCardModal, setShowSaveCardModal] = useState(false);
    const isCloverEnabled = process.env.REACT_APP_CLOVER_ENABLED === 'true';

    // 🔒 FEATURE FLAG: Limit Clover card-on-file to specific users during rollout
    const currentUsername = getCurrentUser();

    const canAccessCloverFeature = isCloverEnabled &&
        CLOVER_ALLOWED_USERS.includes(currentUsername?.toLowerCase());

    const handleOrderNoUpdate = (newOrderNo) => {
        console.log('🔄 Updating order number from temp to real:', newOrderNo);
        if (onOrderNoUpdate) {
            onOrderNoUpdate(newOrderNo);
        }
    };



    const paymentCalculations = usePaymentCalculations(orderItems, accessories, customerInfo, savedPaymentData);
    const agreementManager = useAgreementManager(
        customerInfo,
        orderNo,
        paymentCalculations.totals,
        paymentCalculations.paymentSummary,
        null, // ← orderSubmissionUtils - pass null initially
        handleOrderNoUpdate,
        wigDetails,
        savedPaymentData?.agreementState // 🔧 NEW: Restore previous agreement state
    );

    // ✅ Create orderSubmission with agreementManager
    const orderSubmission = useOrderSubmission({
        customerInfo,
        wigDetails,
        wigData,
        wigRoomsData,
        orderItems,
        accessories,
        barcode,
        serialCode,
        orderNo,
        paymentCalculations,
        agreementManager,
        appointmentId
    });

        
//    const currentWarehouse = getUserWarehouse(orderSubmission.getCurrentUser());
    
    // Validation using service
    const validateSubmission = () => {
        const validation = validationService.validateOrder({
            paymentCalculations,
            agreementManager,
            customerInfo,
            isIsraelUser: isIsraelUser(),
            orderItems,
            accessories,
            requiresAgreement: requiresAgreement(customerInfo?.orderType),
            depositBypassActive
        });

        if (!validation.isValid) {
            setError(validation.error);
            
            // Handle specific validation actions
            if (validation.action === 'showTaxSelector') {
                setShowTaxSelector(true);
            }
            if (validation.action === 'showAgreement') {
                agreementManager.openInPersonSigning();
            }
        }

        return validation.isValid;
    };



    const validateWarrantyRequirements = () => {
        // No longer validate based on order type alone
        // Warranty will be checked by barcode when viewing the order
        return true; // Always allow orders to proceed
      };

    // Updated handleSubmit function
    const handleSubmit = async () => {
        // 🔧 CRITICAL: Check if order still exists before submitting
        // This prevents duplicate submissions when order was already submitted in another tab
        if (currentOrderId && !orderExists(currentOrderId)) {
            alert('This order has already been submitted or closed in another tab/window. Please start a new order.');
            setError('Order no longer exists - it may have been submitted in another tab.');
            return;
        }

        if (!validateSubmission()) {
            return;
        }

        // Validate warranty requirements
        if (!validateWarrantyRequirements()) {
            setError('Warranty validation failed');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            // 🆕 CLOVER: Process Clover payments for each payment method with saved card
            const cloverPayments = paymentCalculations.payments.filter(p => p.savedCardId && isCloverEnabled);

            if (cloverPayments.length > 0) {
                console.log(`💳 Processing ${cloverPayments.length} Clover payment(s) before order submission...`);

                const currentUser = getCurrentUser();

                for (const payment of cloverPayments) {
                    try {
                        // Always compute from components to guarantee the fee is included.
                        // DO NOT use payment.totalCharge as the sole source — it can be stale/incorrect,
                        // causing the fee to be silently dropped even when the UI shows it correctly.
                        const baseAmount = parseFloat(payment.amount) || 0;
                        const taxOnPayment = parseFloat(payment.taxOnPayment) || 0;
                        // Use stored fee; if missing, recalculate to be safe
                        const storedFee = parseFloat(payment.cardFee) || 0;
                        const shouldHaveFee = payment.method === 'Credit Card' && (baseAmount + taxOnPayment) >= 150 && !isIsraelUser();
                        const cardFee = storedFee || (shouldHaveFee ? parseFloat(((baseAmount + taxOnPayment) * 0.03).toFixed(2)) : 0);
                        const totalChargeAmount = parseFloat((baseAmount + taxOnPayment + cardFee).toFixed(2));
                        if (totalChargeAmount <= 0) {
                            console.log(`⏭️ Skipping $0 Clover charge — payment will be collected later`);
                            continue;
                        }

                        console.log(`💳 Charging saved card ${payment.savedCardId} for $${totalChargeAmount}...`);
                        console.log(`   Base: $${payment.amount}, Tax: $${payment.taxOnPayment || 0}, Fee: $${payment.cardFee || 0}`);

                        const chargeResponse = await axios.post(`${API_URL}/api/clover/charge-card`, {
                            paymentMethodId: payment.savedCardId,
                            amount: totalChargeAmount,
                            orderNo: orderNo,
                            username: currentUser?.username || 'SYSTEM'
                        });

                        if (!chargeResponse.data.success) {
                            throw new Error(chargeResponse.data.error || 'Failed to charge saved card');
                        }

                        console.log('✅ Clover card charged successfully:', chargeResponse.data);

                        // Store cloverTransactionId on the payment for backend tracking
                        payment.cloverTransactionId = chargeResponse.data.transaction.transactionId;
                        payment.isCardOnFile = true;

                        // Update payment notes with transaction info
                        paymentCalculations.handlePaymentNotesChange(
                            payment.id,
                            `Clover saved card - ${chargeResponse.data.transaction.card.brand} ••${chargeResponse.data.transaction.card.lastFour} - Transaction ID: ${chargeResponse.data.transaction.transactionId}`
                        );

                    } catch (cloverError) {
                        console.error('❌ Clover payment failed:', cloverError);
                        setError(`Card payment failed: ${cloverError.response?.data?.error || cloverError.message}`);
                        setIsSubmitting(false);
                        return;
                    }
                }

                console.log(`✅ All ${cloverPayments.length} Clover payment(s) processed successfully`);
            }

            // Log warranty info before submission
            const warrantyOrderTypes = ['New Wig'];
            if (warrantyOrderTypes.includes(normalizeOrderType(customerInfo.orderType))) {
                console.log('🛡️ This order will include 1-year warranty:', {
                    orderType: customerInfo.orderType,
                    warrantyDuration: '365 days',
                    startDate: 'Order submission date'
                });
            }

            const result = await orderSubmission.submitOrder({
                depositBypassReason: depositBypassActive ? depositBypassReason : null
            });

            if (result.success) {
                // Update payment summary with final results
                paymentCalculations.setPaymentSummary(prev => ({
                    ...prev,
                    isComplete: true,
                    invoiceNumber: result.invoiceNumber,
                    ...result.summaryUpdates
                }));

                // Call parent callback to trigger order closure
                onSubmit(result);

                // 🔧 FIX: Wait for order closure to complete before navigating
                // This ensures the order tab is properly closed in localStorage
                // before we navigate away to the work order profile
                await new Promise(resolve => setTimeout(resolve, 500));

                // Now navigate to order profile
                navigate(`/work-order-profile/${result.orderNo}`);
            } else {
                setError(result.error);
            }
        } catch (error) {
            console.error('Order submission failed:', error);
            setError(`Order submission failed: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    // 🔧 CRITICAL: Immediate save when agreement is signed (no debounce)
    useEffect(() => {
        if (onStateChange && agreementManager.agreementState?.isSigned) {
            console.log('💾 IMMEDIATE SAVE: Agreement signed, saving without debounce');
            onStateChange({
                paymentData: {
                    payments: paymentCalculations.payments,
                    paymentSummary: paymentCalculations.paymentSummary,
                    agreementState: agreementManager.agreementState
                },
                wigRoomsData
            });
        }
    }, [agreementManager.agreementState?.isSigned]); // Only fire when isSigned changes

    // 🔧 NEW: Auto-save payment and agreement state changes back to parent (debounced)
    useEffect(() => {
        if (onStateChange) {
            const debounceTimeout = setTimeout(() => {
                onStateChange({
                    paymentData: {
                        payments: paymentCalculations.payments,
                        paymentSummary: paymentCalculations.paymentSummary,
                        agreementState: agreementManager.agreementState // 🔧 Save agreement state
                    },
                    wigRoomsData
                });
                // console.log('💾 Payment page state auto-saved (including agreement)');
            }, 500); // Quick save for critical payment data

            return () => clearTimeout(debounceTimeout);
        }
    }, [
        paymentCalculations.payments,
        paymentCalculations.paymentSummary,
        agreementManager.agreementState, // 🔧 Watch for agreement changes
        wigRoomsData,
        onStateChange
    ]);

    // 🆕 CLOVER: Fetch saved cards when component mounts
    useEffect(() => {
        const fetchSavedCards = async () => {
            const customerId = customerInfo?.customerNo || customerInfo?.ID;
            if (!customerId || !isCloverEnabled) {
                console.log('Skipping Clover fetch:', { customerNo: customerInfo?.customerNo, ID: customerInfo?.ID, isCloverEnabled });
                return;
            }

            try {
                console.log(`💳 Fetching saved cards for customer ${customerId}...`);
                const response = await axios.get(`${API_URL}/api/clover/customer/${customerId}/cards`);

                if (response.data.success) {
                    setCloverCards(response.data.cards || []);
                    console.log(`✅ Loaded ${response.data.count} saved card(s)`);
                }
            } catch (error) {
                console.error('❌ Error fetching saved cards:', error.message);
            }
        };

        fetchSavedCards();
    }, [customerInfo?.customerNo, customerInfo?.ID, isCloverEnabled]);


    const handlePreviousClick = () => {
        // 🔧 UPDATED: Include agreement state when navigating back
        onPrevious({
            paymentData: {
                payments: paymentCalculations.payments,
                paymentSummary: paymentCalculations.paymentSummary,
                agreementState: agreementManager.agreementState // 🔧 Include agreement state
            },
            wigRoomsData
        });
    };

    // Submit button state
    const getSubmitButtonState = () => {
        if (isSubmitting) {
            return { 
                disabled: true, 
                text: isIsraelUser() ? 'Processing Israel Order...' : 'Processing Order...' 
            };
        }

        const totalItems = (orderItems?.length || 0) + (accessories?.length || 0);
        if (totalItems === 0) {
            return { 
                disabled: true, 
                text: 'Add Items to Order' 
            };
        }

        if (!depositBypassActive && !paymentCalculations.paymentRequirementMet) {
            return {
                disabled: true,
                text: 'Payment Requirement Not Met'
            };
        }

        if (!paymentCalculations.taxRequirementMet) {
            return { 
                disabled: true, 
                text: 'Select Tax Location First' 
            };
        }

        if (requiresAgreement(customerInfo?.orderType) && agreementManager.isRequired && !agreementManager.isSigned) {
            return { 
                disabled: true, 
                text: 'Customer Agreement Required' 
            };
        }

        if (paymentCalculations.paymentSummary.isComplete) {
            return { 
                disabled: true, 
                text: 'Order Complete' 
            };
        }

        const hasBalance = parseFloat(paymentCalculations.paymentSummary.balanceDue) > 0;
        const orderText = isIsraelUser() ? 'Israel Order' : 'Order';
        
        return { 
            disabled: false, 
            text: hasBalance ? `Complete ${orderText} (with Balance)` : `Complete ${orderText}` 
        };
    };

    const submitButtonState = getSubmitButtonState();

    return (
        <div className="woe-container slide-in">
            <h2>Payment & Checkout</h2>
            
            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}


{/* Customer & Order Details Section */}
<div className="customer-details-section" style={{
    backgroundColor: 'var(--light-bg)',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '25px'
}}>
    <h3 style={{ marginTop: '0', marginBottom: '15px', color: '#495057' }}>
        Order Details - #{orderNo}
    </h3>
    
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
        {/* Customer Information */}
        <div className="detail-group">
            <h4 style={{ marginBottom: '8px', color: '#6c757d', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Customer Information
            </h4>
            <div className="detail-item">
                <strong>Name:</strong> {customerInfo?.customerName || 'Not provided'}
            </div>
            <div className="detail-item">
                <strong>Phone:</strong> {customerInfo?.phone || 'Not provided'}
            </div>
            <div className="detail-item">
                <strong>Email:</strong> {customerInfo?.email || 'Not provided'}
            </div>
            <div className="detail-item">
                <strong>Address:</strong> {customerInfo?.address || 'Not provided'}
            </div>
            {customerInfo?.moldId && (
                <div className="detail-item">
                    <strong>Mold ID:</strong> {customerInfo.moldId}
                </div>
            )}
        </div>

        {/* Customer ID Information - Critical for debugging */}
        <div className="detail-group">
            <h4 style={{ marginBottom: '8px', color: '#6c757d', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Customer ID Information
            </h4>
            <div className="detail-item" style={{ 
                backgroundColor: customerInfo?.customerNo ? '#d4eddad5' : '#f8d7da',
                padding: '8px',
                borderRadius: '4px',
                border: `1px solid ${customerInfo?.customerNo ? '#c3e6cb' : '#f5c6cb'}`
            }}>
                <strong>Customer ID:</strong> {customerInfo?.customerNo || customerInfo?.ID || 'NOT SET ❌'}
                {customerInfo?.customerNo && <span style={{ color: '#28a745', marginLeft: '8px' }}>✅</span>}
            </div>
            <div className="detail-item">
                <strong>ID Field:</strong> {customerInfo?.ID || 'Not set'}
            </div>
            <div className="detail-item">
                <strong>CustomerNo Field:</strong> {customerInfo?.customerNo || 'Not set'}
            </div>
        </div>

        {/* Order Information */}
        <div className="detail-group" >
            <h4 style={{ marginBottom: '8px', color: '#6c757d', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Order Information
            </h4>
            <div className="detail-item">
                <strong>Order Type:</strong> {customerInfo?.orderType || 'Not specified'}
            </div>
            <div className="detail-item">
                <strong>Order Date:</strong> {formatDate(new Date())}
            </div>
            
            {/* <div className="detail-item">
            <strong>Warehouse:</strong> {currentWarehouse}

            {currentWarehouse === 'Israel' && (
                <span style={{ color: '#17a2b8', marginLeft: '8px' }}>🇮🇱</span>
            )}
            {currentWarehouse === 'Lakewood' && (
                <span style={{ color: '#28a745', marginLeft: '8px' }}>🏢</span>
            )}
            {currentWarehouse === 'Pomona' && (
                <span style={{ color: '#ffc107', marginLeft: '8px' }}>🏭</span>
            )}
            </div> */}
            <div className="detail-item" style={{
                backgroundColor: orderSubmission.getCurrentUser() !== 'System' ? '#d4eddad5' : '#fff3cd',
                padding: '6px 8px',
                borderRadius: '4px',
                border: `1px solid ${orderSubmission.getCurrentUser() !== 'System' ? '#c3e6cb' : '#ffeaa7'}`
            }}>
                <strong>Entered By:</strong> {orderSubmission.getCurrentUser()}
                {orderSubmission.getCurrentUser() !== 'System' && <span style={{ color: '#28a745', marginLeft: '8px' }}>✅</span>}
                {orderSubmission.getCurrentUser() === 'System' && <span style={{ color: '#856404', marginLeft: '8px' }}>⚠️</span>}
            </div>
            {barcode && (
              <div className="detail-item" style={{
                    backgroundColor: barcode ? '#d4eddad5' : '#f8d7da',
                    padding: '6px 8px',
                    borderRadius: '4px',
                    border: `1px solid ${barcode ? '#c3e6cb' : '#f5c6cb'}`
                }}>
                    <strong>Barcode:</strong> {barcode || 'Missing'}
                    {barcode && <span style={{ color: '#28a745', marginLeft: '8px' }}>✅</span>}
                    {!barcode && <span style={{ color: '#dc3545', marginLeft: '8px' }}>❌</span>}
                </div>
            )}
            {serialCode && (
                <div className="detail-item">
                    <strong>Serial Code:</strong> {serialCode}
                </div>
            )}
        </div>

        {/* Integration Status */}
        <div className="detail-group">
            <h4 style={{ marginBottom: '8px', color: '#6c757d', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Integration Status
            </h4>
            <div className="detail-item">
                <strong>Location:</strong> {isIsraelUser() ? '🇮🇱 Israel' : '🇺🇸 US'}
            </div>
            <div className="detail-item">
                <strong>Processing Mode:</strong> Local Processing
            </div>
            <div className="detail-item">
                <strong>Tax Required:</strong> {requiresTaxCalculation(customerInfo?.orderType) && !isIsraelUser() ? 'Yes' : 'No'}
            </div>
        </div>
    </div>

   
   

    {/* Warning if Customer ID is missing */}
    {!customerInfo?.customerNo && !customerInfo?.ID && (
        <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '6px',
            padding: '12px',
            marginTop: '15px',
            color: '#856404'
        }}>
            <strong>⚠️ Warning:</strong> Customer ID is missing! This will cause the OrderCustomerNo to be NULL in the database.
            <br />
            <small>Check that the customer ID is being passed from WOE.jsx through the component chain.</small>
        </div>
    )}

 
 
</div>

            {/* Location indicator for Israel users */}
            {isIsraelUser() && (
                <div className="israel-notice" style={{
                    backgroundColor: '#e8f4fd',
                    border: '1px solid #007bff',
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '20px',
                    color: '#0056b3',
                    textAlign: 'center'
                }}>
                    🇮🇱 <strong>Israel Location</strong> - Modular system active, local processing mode
                </div>
            )}

          
          

            

            {/* Order Items Section - Using your CSS */}
            <div className="order-items-section">
                <h3>Order Summary</h3>
                
                <table className="summary-table">
                    <thead>
                        <tr>
                            <th>Item</th>
                            <th>Description</th>
                            <th>Quantity</th>
                            <th>Price</th>
                            <th>Subtotal</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orderItems.map((item, index) => (
                            <tr key={index}>
                                <td>{item.type || 'Item'}</td>
                                <td>{item.description}</td>
                                <td className="text-right">{item.quantity || 1}</td>
                                <td className="text-right">${paymentCalculations.formatCurrency(item.price)}</td>
                                <td className="text-right">${paymentCalculations.formatCurrency(item.price * (item.quantity || 1))}</td>
                            </tr>
                        ))}
                        {accessories.map((acc, index) => (
                            <tr key={`acc-${index}`}>
                                <td>{acc.name || 'Accessory'}</td>
                                <td>{acc.description}</td>
                                <td className="text-right">{acc.quantity || 1}</td>
                                <td className="text-right">${paymentCalculations.formatCurrency(acc.price)}</td>
                                <td className="text-right">${paymentCalculations.formatCurrency(acc.price * (acc.quantity || 1))}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan="4" className="text-right"><strong>Subtotal:</strong></td>
                            <td className="text-right">${paymentCalculations.formatCurrency(paymentCalculations.totals.subtotal)}</td>
                        </tr>
                        
                        {/* Tax Row with Location Selector */}
                        <tr>
                            <td colSpan="4" className="text-right">
                                <div className="tax-row">
                                    <strong>Tax{paymentCalculations.totals.taxRate > 0 ? ` (${paymentCalculations.totals.taxRate}%)` : ''}:</strong>
                                    
                                    {/* Tax Location Container for non-Israel users */}
                                    {!isIsraelUser() && requiresTaxCalculation(customerInfo?.orderType) && (
                                        <div className="tax-location-container">
                                            {paymentCalculations.userLocation && (
                                                <span className="tax-location-info">
                                                    Based on: {paymentCalculations.userLocation}
                                                </span>
                                            )}
                                            <button 
                                                type="button" 
                                                className="lookup-tax-btn"
                                                onClick={() => setShowTaxSelector(true)}
                                                disabled={isLoadingUserLocation}
                                            >
                                                {isLoadingUserLocation ? 'Looking up...' : paymentCalculations.userLocation ? 'Change Tax Location' : 'Select Tax Location'}
                                            </button>
                                        </div>
                                    )}
                                    
                                    {/* Israel Tax Note */}
                                    {isIsraelUser() && (
                                        <small className="israel-tax-note">
                                            🇮🇱 Israel location - no tax applicable
                                        </small>
                                    )}
                                </div>
                            </td>
                            <td className="text-right">${paymentCalculations.formatCurrency(paymentCalculations.totals.tax)}</td>
                        </tr>
                        
                        {/* Card Fees Row */}
                        {parseFloat(paymentCalculations.paymentSummary.totalCardFees) > 0 && !isIsraelUser() && (
                            <tr>
                                <td colSpan="4" className="text-right">
                                    <strong>Credit Card Fees (3% for payments over $150):</strong>
                                </td>
                                <td className="text-right">${paymentCalculations.formatCurrency(paymentCalculations.paymentSummary.totalCardFees)}</td>
                            </tr>
                        )}
                        
                        {/* Total Row */}
                        <tr>
                            <td colSpan="4" className="text-right"><strong>TOTAL:</strong></td>
                            <td className="text-right">
                                <strong>${paymentCalculations.formatCurrency(parseFloat(paymentCalculations.totals.total) + parseFloat(paymentCalculations.paymentSummary.totalCardFees || 0))}</strong>
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {/* Room Tasks Section - Using your CSS */}
            <div className="room-tasks-section">
                <h3>Room Assignments</h3>
                <table className="summary-table">
                    <thead>
                        <tr>
                            <th>Room</th>
                            <th>Task</th>
                            <th>Worker</th>
                            <th>Due Date</th>
                            <th>Notes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {wigRoomsData.map((task, index) => (
                            <tr key={index}>
                                <td>{task.roomNo}</td>
                                <td>{task.roomTask}</td>
                                <td>{task.assignedWorker}</td>
                                <td>{task.taskDueDate ? formatDate(task.taskDueDate) : 'Not set'}</td>
                                <td>{task.taskNotes}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Payment Information Section - Using your CSS */}
            {/* Payment Information Section - FIXED VERSION */}
                <div className="payment-section">
                    <h3>Payment Information</h3>

                    {/* Multiple Payment Methods */}
                    {paymentCalculations.payments.map((payment, index) => (
                        <div key={payment.id} className="payment-method-container">
                            <div className="payment-method-header">
                                <h4>Payment Method {index + 1}</h4>
                                {paymentCalculations.payments.length > 1 && (
                                    <button 
                                        type="button" 
                                        className="remove-payment-btn"
                                        onClick={() => paymentCalculations.removePayment(payment.id)}
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                            
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Payment Type</label>
                                    <select
                                        value={payment.method}
                                        onChange={(e) => paymentCalculations.handlePaymentMethodChange(payment.id, e.target.value)}
                                        className="form-control"
                                    >
                                        <option value="Credit Card">Credit Card</option>
                                        <option value="Cash">Cash</option>
                                        <option value="Check">Check</option>
                                        <option value="Zelle">Zelle</option>
                                        <option value="Bank Transfer">Bank Transfer</option>
                                        <option value="Venmo">Venmo</option>
                                        <option value="Debit Card">Debit Card</option>
                                    </select>
                                </div>
                                
                                <div className="form-group">
                                    <label>Amount Toward Order {!isIsraelUser() && requiresTaxCalculation(customerInfo?.orderType) && '(tax will be added)'}</label>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                                        <input
                                            type="number"
                                            value={payment.amount}
                                            onChange={(e) => paymentCalculations.handlePaymentAmountChange(payment.id, e.target.value)}
                                            className="form-control"
                                            onWheel={(e) => e.target.blur()}
                                            min="0"
                                            step="0.01"
                                            style={{ flex: 1 }}
                                            placeholder={!isIsraelUser() && requiresTaxCalculation(customerInfo?.orderType) ? 
                                                "Enter amount toward subtotal" : 
                                                "Enter payment amount"
                                            }
                                        />
                                        {index === 0 && (
                                            <button
                                                type="button"
                                                onClick={paymentCalculations.handlePayInFull}
                                                className="lookup-button"
                                                style={{ 
                                                    whiteSpace: 'nowrap',
                                                    fontSize: '12px',
                                                    padding: '8px 12px',
                                                    height: 'fit-content'
                                                }}
                                                title="Pay remaining subtotal balance"
                                            >
                                                Pay Remaining
                                            </button>
                                        )}
                                    </div>
                                    {index === 0 && (
                                        <small style={{ color: '#999', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                                            Remaining subtotal: ${paymentCalculations.formatCurrency(Math.max(0, parseFloat(paymentCalculations.totals.subtotal) - paymentCalculations.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)))}
                                            {!isIsraelUser() && requiresTaxCalculation(customerInfo?.orderType) && (
                                                <span> (tax will be calculated automatically)</span>
                                            )}
                                        </small>
                                    )}
                                </div>
                                
                                <div className="form-group full-width">
                                    <label>Notes</label>
                                    <input
                                        type="text"
                                        value={payment.notes}
                                        onChange={(e) => paymentCalculations.handlePaymentNotesChange(payment.id, e.target.value)}
                                        className="form-control"
                                        placeholder="Card last 4 digits, check #, etc."
                                    />
                                </div>

                                {/* 🆕 CLOVER: Saved Cards Dropdown for Credit/Debit Card */}
                                {/* Feature flag: Only show if card-on-file is enabled */}
                                {canAccessCloverFeature && (payment.method === 'Credit Card' || payment.method === 'Debit Card') && cloverCards.length > 0 && (
                                    <div style={{
                                        backgroundColor: '#f0f8ff',
                                        border: '2px solid #007bff',
                                        borderRadius: '8px',
                                        padding: '15px',
                                        marginTop: '15px',
                                        marginBottom: '10px'
                                    }}>
                                        <h4 style={{ marginTop: '0', marginBottom: '12px', color: '#007bff' }}>
                                            <i className="fas fa-credit-card" style={{ marginRight: '8px' }}></i>
                                            Use Saved Card
                                        </h4>

                                        <div className="form-group" style={{ marginBottom: '0' }}>
                                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500' }}>
                                                Select a saved card or add a new one below:
                                            </label>
                                            <select
                                                value={savedCardSelections[payment.id] || ''}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === 'none' || value === '') {
                                                        // Clear saved card selection for this payment
                                                        setSavedCardSelections(prev => {
                                                            const updated = { ...prev };
                                                            delete updated[payment.id];
                                                            return updated;
                                                        });
                                                        paymentCalculations.handlePaymentNotesChange(payment.id, '');
                                                        payment.savedCardId = null;
                                                    } else {
                                                        const cardId = parseInt(value);
                                                        const selectedCard = cloverCards.find(c => c.PaymentMethodId === cardId);
                                                        if (selectedCard) {
                                                            // Set saved card selection for this payment
                                                            setSavedCardSelections(prev => ({
                                                                ...prev,
                                                                [payment.id]: cardId
                                                            }));
                                                            payment.savedCardId = cardId;
                                                            // Update notes with card info
                                                            paymentCalculations.handlePaymentNotesChange(
                                                                payment.id,
                                                                `Clover saved card - ${selectedCard.CardBrand} ••${selectedCard.Last4}`
                                                            );
                                                        }
                                                    }
                                                }}
                                                className="form-control"
                                                style={{ fontSize: '14px' }}
                                            >
                                                <option value="none">Add a new card (see options below)</option>
                                                {cloverCards.map(card => (
                                                    <option key={card.PaymentMethodId} value={card.PaymentMethodId}>
                                                        {card.CardBrand} •••• {card.Last4} - Exp {card.ExpMonth}/{card.ExpYear}
                                                        {card.IsDefault ? ' (Default)' : ''}
                                                        {card.IsExpired ? ' - EXPIRED' : ''}
                                                    </option>
                                                ))}
                                            </select>

                                            {savedCardSelections[payment.id] && (
                                                <div style={{
                                                    backgroundColor: '#d4edda',
                                                    border: '1px solid #c3e6cb',
                                                    borderRadius: '4px',
                                                    padding: '10px',
                                                    marginTop: '10px',
                                                    fontSize: '13px',
                                                    color: '#155724'
                                                }}>
                                                    <i className="fas fa-check-circle" style={{ marginRight: '6px' }}></i>
                                                    This card will be charged <strong>${(parseFloat(payment.totalCharge) || parseFloat(payment.amount) || 0).toFixed(2)}</strong> when you submit the order
                                                    {parseFloat(payment.cardFee || 0) > 0 && (
                                                        <div style={{ fontSize: '11px', marginTop: '5px', color: '#155724' }}>
                                                            (Includes ${payment.amount} base + ${(parseFloat(payment.taxOnPayment) || 0).toFixed(2)} tax + ${(parseFloat(payment.cardFee) || 0).toFixed(2)} processing fee)
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* 🆕 CLOVER: Save Card Button */}
                                {canAccessCloverFeature && (payment.method === 'Credit Card' || payment.method === 'Debit Card') && !savedCardSelections[payment.id] && (
                                    <div style={{ marginTop: '15px' }}>
                                        <button
                                            type="button"
                                            onClick={() => setShowSaveCardModal(true)}
                                            style={{
                                                backgroundColor: '#fff',
                                                color: '#007bff',
                                                border: '2px solid #007bff',
                                                padding: '10px 16px',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: '500'
                                            }}
                                        >
                                            <i className="fas fa-credit-card" style={{ marginRight: '8px' }}></i>
                                            Save Card for Future Use
                                        </button>
                                    </div>
                                )}

                                {showSaveCardModal && (
                                    <SaveCardModal
                                        customerId={customerInfo.customerNo || customerInfo.ID}
                                        currentUsername={getCurrentUser()}
                                        onClose={() => setShowSaveCardModal(false)}
                                        onCardSaved={async () => {
                                            const customerId = customerInfo.customerNo || customerInfo.ID;
                                            const cardsResponse = await axios.get(`${API_URL}/api/clover/customer/${customerId}/cards`);
                                            if (cardsResponse.data.success) {
                                                setCloverCards(cardsResponse.data.cards);
                                            }
                                            setShowSaveCardModal(false);
                                        }}
                                    />
                                )}
                            </div>

                            {/* Tax and Fee Breakdown for non-Israel users */}
                            {!isIsraelUser() && (parseFloat(payment.taxOnPayment || 0) > 0 || parseFloat(payment.cardFee || 0) > 0) && (
                                <div className="payment-breakdown-container" style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    padding: '12px',
                                    marginTop: '10px'
                                }}>
                                    <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#495057' }}>
                                        Charge Breakdown:
                                    </div>
                                    
                                    {/* Base Amount */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px', color: '#333333' }}>
                                        <span>Toward Order:</span>
                                        <span>${paymentCalculations.formatCurrency(payment.amount || 0)}</span>
                                    </div>
                                    
                                    {/* Tax Calculation */}
                                    {parseFloat(payment.taxOnPayment || 0) > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px', color: '#6c757d' }}>
                                            <span>+ Tax ({paymentCalculations.totals.taxRate}%):</span>
                                            <span>${(parseFloat(payment.taxOnPayment)).toFixed(2)}</span>
                                        </div>
                                    )}
                                    
                                    {/* Subtotal before fees */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '8px', color: '#333333', paddingTop: '4px', borderTop: '1px solid #dee2e6' }}>
                                        <span><strong>Subtotal:</strong></span>
                                        <span><strong>${((parseFloat(payment.amount) || 0) + (parseFloat(payment.taxOnPayment) || 0)).toFixed(2)}</strong></span>
                                    </div>
                                    
                                    {/* Card Fee */}
                                    {parseFloat(payment.cardFee || 0) > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px', color: '#dc3545' }}>
                                            <span>+ Processing Fee (3%):</span>
                                            <span>${(parseFloat(payment.cardFee)).toFixed(2)}</span>
                                        </div>
                                    )}
                                    
                                    <hr style={{ margin: '8px 0', borderColor: '#dee2e6' }} />
                                    
                                    {/* Total Charged */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 'bold', color: '#28a745' }}>
                                        <span>Total to Charge Customer:</span>
                                        <span>${(parseFloat(payment.totalCharge) || parseFloat(payment.amount) || 0).toFixed(2)}</span>
                                    </div>
                                    
                                    <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px', fontStyle: 'italic' }}>
                                        * Customer pays ${payment.amount} toward order + tax + processing fee
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Deposit Bypass — New Wig orders only ("Aidel New Wig" included) */}
                    {normalizeOrderType(customerInfo?.orderType) === 'New Wig' && (
                        <div style={{
                            border: depositBypassActive ? '2px solid #dc3545' : '1px solid #ffc107',
                            borderRadius: '6px',
                            padding: '14px 16px',
                            marginBottom: '20px',
                            backgroundColor: depositBypassActive ? '#fff5f5' : '#fffbea'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                                <div>
                                    <strong style={{ color: depositBypassActive ? '#dc3545' : '#856404' }}>
                                        {depositBypassActive ? 'Deposit Bypass ACTIVE' : 'Bypass Deposit Requirement'}
                                    </strong>
                                    {depositBypassActive && (
                                        <div style={{ fontSize: '12px', color: '#dc3545', marginTop: '3px' }}>
                                            Reason: {depositBypassReason}
                                        </div>
                                    )}
                                    {!depositBypassActive && (
                                        <div style={{ fontSize: '12px', color: '#856404', marginTop: '3px' }}>
                                            Allows any deposit amount instead of the $2,500 minimum.
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (depositBypassActive) {
                                            setDepositBypassActive(false);
                                            setDepositBypassReason('');
                                            setBypassReasonInput('');
                                            setShowBypassModal(false);
                                        } else {
                                            setShowBypassModal(true);
                                            setBypassReasonInput('');
                                        }
                                    }}
                                    style={{
                                        backgroundColor: depositBypassActive ? '#dc3545' : '#ffc107',
                                        color: depositBypassActive ? '#fff' : '#212529',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '7px 14px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {depositBypassActive ? 'Remove Bypass' : 'Enable Bypass'}
                                </button>
                            </div>

                            {/* Inline reason entry */}
                            {showBypassModal && !depositBypassActive && (
                                <div style={{ marginTop: '14px', borderTop: '1px solid #ffc107', paddingTop: '14px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#856404' }}>
                                        Reason for bypassing the $2,500 deposit requirement (minimum 10 characters):
                                    </label>
                                    <textarea
                                        value={bypassReasonInput}
                                        onChange={(e) => setBypassReasonInput(e.target.value)}
                                        rows={3}
                                        style={{
                                            width: '100%',
                                            border: bypassReasonInput.length > 0 && bypassReasonInput.length < 10 ? '1px solid #dc3545' : '1px solid #ced4da',
                                            borderRadius: '4px',
                                            padding: '8px',
                                            fontSize: '13px',
                                            resize: 'vertical',
                                            boxSizing: 'border-box'
                                        }}
                                        placeholder="Describe why the deposit requirement is being bypassed..."
                                        autoFocus
                                    />
                                    {bypassReasonInput.length > 0 && bypassReasonInput.length < 10 && (
                                        <div style={{ fontSize: '12px', color: '#dc3545', marginTop: '4px' }}>
                                            {10 - bypassReasonInput.length} more character(s) required.
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                        <button
                                            type="button"
                                            disabled={bypassReasonInput.trim().length < 10}
                                            onClick={() => {
                                                setDepositBypassReason(bypassReasonInput.trim());
                                                setDepositBypassActive(true);
                                                setShowBypassModal(false);
                                            }}
                                            style={{
                                                backgroundColor: bypassReasonInput.trim().length >= 10 ? '#dc3545' : '#ccc',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '4px',
                                                padding: '8px 18px',
                                                fontWeight: '600',
                                                cursor: bypassReasonInput.trim().length >= 10 ? 'pointer' : 'not-allowed',
                                                fontSize: '13px'
                                            }}
                                        >
                                            Confirm Bypass
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setShowBypassModal(false); setBypassReasonInput(''); }}
                                            style={{
                                                backgroundColor: '#c53030',
                                                border: '1px solid #c53030',
                                                borderRadius: '4px',
                                                padding: '8px 14px',
                                                cursor: 'pointer',
                                                fontSize: '13px'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ADD PAYMENT BUTTON - This was missing! */}
                    <div className="add-payment-container" style={{ marginBottom: '20px' }}>
                        <button
                            type="button"
                            onClick={paymentCalculations.addPayment}
                            className="add-payment-btn"
                            style={{
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            + Add Another Payment Method
                        </button>
                    </div>

                    {/* SINGLE Payment Summary Section */}
                    <div className="payment-summary">
                        <div className="summary-row">
                            <span className="summary-label">Order Subtotal:</span>
                            <span className="summary-value">${paymentCalculations.formatCurrency(paymentCalculations.totals.subtotal)}</span>
                        </div>
                        <div className="summary-row">
                            <span className="summary-label">Paid Toward Subtotal:</span>
                            <span className="summary-value">${paymentCalculations.paymentSummary.totalPaid}</span>
                        </div>
                        <div className="summary-row">
                            <span className="summary-label">Order Tax:</span>
                            <span className="summary-value">${paymentCalculations.formatCurrency(paymentCalculations.totals.tax)}</span>
                        </div>
                        {parseFloat(paymentCalculations.paymentSummary.totalCardFees || 0) > 0 && (
                            <div className="summary-row">
                                <span className="summary-label">Total Processing Fees:</span>
                                <span className="summary-value">${paymentCalculations.paymentSummary.totalCardFees}</span>
                            </div>
                        )}
                        <div className="summary-row" style={{ borderTop: '1px solid #dee2e6', paddingTop: '8px', marginTop: '8px' }}>
                            <span className="summary-label"><strong>Total Charged to Customer:</strong></span>
                            <span className="summary-value"><strong>${paymentCalculations.paymentSummary.totalCharged}</strong></span>
                        </div>
                        
                        {/* Balance status */}
                        <div className="summary-row">
                            {paymentCalculations.paymentSummary.paymentStatus === "BALANCE DUE" && (
                                <>
                                    <span className="summary-label">Subtotal Balance Due:</span>
                                    <span className="summary-value balance-due">
                                        ${paymentCalculations.paymentSummary.balanceDue}
                                    </span>
                                </>
                            )}
                            {paymentCalculations.paymentSummary.paymentStatus === "CHANGE DUE" && (
                                <>
                                    <span className="summary-label">Change Due:</span>
                                    <span className="summary-value change-due">
                                        ${paymentCalculations.paymentSummary.changeDue}
                                    </span>
                                </>
                            )}
                            {paymentCalculations.paymentSummary.paymentStatus === "PAID IN FULL" && (
                                <>
                                    <span className="summary-label">Payment Status:</span>
                                    <span className="summary-value paid-in-full">
                                        ✅ Subtotal Paid in Full
                                    </span>
                                </>
                            )}
                        </div>
                        
                        {/* Payment requirements */}
                        {paymentCalculations.getPaymentRequirementText() && !depositBypassActive && (
                            <div className={`payment-requirement-status ${paymentCalculations.paymentRequirementMet ? 'success' : 'error'}`}>
                                <span>{paymentCalculations.paymentRequirementMet ? '✅' : '⚠️'} {paymentCalculations.getPaymentRequirementText()}</span>
                                {paymentCalculations.paymentRequirementMet && (
                                    <div style={{ fontSize: '12px', marginTop: '5px' }}>
                                        ✓ Payment requirement met! You can proceed.
                                    </div>
                                )}
                            </div>
                        )}
                        {depositBypassActive && (
                            <div className="payment-requirement-status success" style={{ borderColor: '#dc3545', backgroundColor: '#fff5f5', color: '#dc3545' }}>
                                ⚠️ Deposit requirement bypassed — any amount accepted.
                            </div>
                        )}
                    </div>
                </div>

            {/* Tax Selector Modal - Using your CSS */}
            {showTaxSelector && !isIsraelUser() && (
                <div className="tax-selector-overlay">
                    <div className="tax-selector-modal">
                        <h4>Select Your Work Location for Tax Rate</h4>
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                            Tax is based on where you are working, not the customer's address.
                        </p>
                        
                        <div className="location-buttons">
                            <button 
                                onClick={() => {
                                    paymentCalculations.handleLocationSelect('NY');
                                    setShowTaxSelector(false);
                                }}
                                className="location-btn"
                            >
                                New York / Pomona (8.375%)
                            </button>
                            <button 
                                onClick={() => {
                                    paymentCalculations.handleLocationSelect('NJ');
                                    setShowTaxSelector(false);
                                }}
                                className="location-btn"
                            >
                                New Jersey / Lakewood (6.625%)
                            </button>
                            <button 
                                onClick={() => {
                                    paymentCalculations.handleLocationSelect('OTHER');
                                    setShowTaxSelector(false);
                                }}
                                className="location-btn"
                            >
                                Other Location (0%)
                            </button>
                        </div>
                        
                        <button 
                            className="cancel-btn"
                            onClick={() => setShowTaxSelector(false)}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}



{/* Agreement Section */}
{requiresAgreement(customerInfo?.orderType) && agreementManager.isRequired && (
    <div className="agreement-section" style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '6px' }}>
        <h3>Customer Agreement</h3>
        <p><strong>Status:</strong> {agreementManager.agreementState.status}</p>
        
        {/* Show processing status */}
        {agreementManager.agreementState.status === 'processing_signature' && (
            <div style={{ color: '#007bff', fontWeight: 'bold' }}>
                📄 Saving agreement to Google Drive...
            </div>
        )}
        
        {!agreementManager.isSigned && (
            <div>
                <button 
                    onClick={agreementManager.sendSMSAgreement}
                    style={{ marginRight: '10px', padding: '8px 16px', fontSize: '14px' }}
                    disabled={!customerInfo?.phone}
                >
                    📱 Send SMS Agreement
                </button>
                
                <button 
                    onClick={() => agreementManager.openInPersonSigning('user_choice')}
                    style={{ padding: '8px 16px' }}
                >
                    📋 Sign In-Person
                </button>
            </div>
        )}
        
        {agreementManager.isSigned && (
            <div>
                <div style={{ color: '#28a745', fontWeight: 'bold', marginBottom: '10px' }}>
                    ✅ Agreement Signed via {agreementManager.agreementState.signingMethod}
                </div>
                
                {/* Show Google Drive status */}
                {agreementManager.agreementState.googleDriveFile && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        📄 Saved to Google Drive: 
                        <a 
                            href={agreementManager.agreementState.googleDriveFile.webViewLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ marginLeft: '5px' }}
                        >
                            View Agreement
                        </a>
                    </div>
                )}
                
                {/* Show Google Drive error if any */}
                {agreementManager.agreementState.googleDriveError && (
                    <div style={{ fontSize: '12px', color: '#ffc107', marginTop: '5px' }}>
                        ⚠️ Google Drive save failed: {agreementManager.agreementState.googleDriveError.message}
                        <br />
                        <small>Agreement is still valid - contact support if needed</small>
                    </div>
                )}
            </div>
        )}
    </div>
)}

            {/* Action Buttons */}
            <div className="button-group">
                <button
                    type="button"
                    className="back-btn"
                    onClick={handlePreviousClick}
                >
                    Back to Room Assignments
                </button>
                
                <button
                    type="button"
                    className="submit-btn"
                    onClick={handleSubmit}
                    disabled={submitButtonState.disabled}
                >
                    {submitButtonState.text}
                </button>
            </div>

            {/* Completion Message */}
            {paymentCalculations.paymentSummary.isComplete && (
                <div className="success-message" style={{ 
                    backgroundColor: '#d4edda', 
                    border: '1px solid #c3e6cb', 
                    borderRadius: '6px', 
                    padding: '20px', 
                    marginTop: '20px' 
                }}>
                    <h3>🎉 {isIsraelUser() ? 'Israel Order' : 'Order'} Successfully Completed!</h3>
                    <p><strong>Invoice Number:</strong> {paymentCalculations.paymentSummary.invoiceNumber}</p>
                    <p><strong>Processing Mode:</strong> {isIsraelUser() ? '🇮🇱 Israel Local' : '🇺🇸 Local'}</p>
                </div>
            )}

            {/* Customer Agreement Modal */}
            <CustomerAgreementModal
                isOpen={agreementManager.agreementState.inPerson.modalOpen}
                onClose={agreementManager.closeInPersonSigning}
                onSign={agreementManager.handleInPersonSignature}
                customerInfo={customerInfo}
                orderData={{
                    orderNo: orderNo,
                    totalAmount: parseFloat(paymentCalculations.totals.total) + parseFloat(paymentCalculations.paymentSummary.totalCardFees || 0)
                }}
                formatCurrency={paymentCalculations.formatCurrency}
            />
        </div>
    );
};

export default WOEPayment;